import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_DISCUSSION_LOAD_BY_ID,
    ACTION_DISCUSSION_LOAD_FAILURE,
    ACTION_DISCUSSION_LOAD_SUCCESS,

    ACTION_DISCUSSION_SAVE,
    ACTION_DISCUSSION_SAVE_SUCCESS,
    ACTION_DISCUSSION_SAVE_FAILURE,

    ACTION_DISCUSSION_CREATE,
    ACTION_DISCUSSION_CREATE_SUCCESS,
    ACTION_DISCUSSION_CREATE_FAILURE,

    ACTION_DISCUSSION_VALIDATE,
    ACTION_DISCUSSION_VALIDATE_SUCCESS,
    ACTION_DISCUSSION_VALIDATE_FAILURE,

    ACTION_DISCUSSION_CLEAR,
    ACTION_DISCUSSION_FILTER,

    DiscussionAction
} from '../actions/discussion';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IDiscussionState {
    formData: Dtos.Discussion | Dtos.Discussion[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.Discussion[];
    formLength: number;
}

const initialState: IDiscussionState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IDiscussionState, _isLoading: boolean): IDiscussionState {
    const newState: IDiscussionState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IDiscussionState,
    formData: Dtos.Discussion | Dtos.Discussion[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IDiscussionState {
    const newState: IDiscussionState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IDiscussionState, responseStatus: Dtos.ResponseStatus): IDiscussionState {
    const newState: IDiscussionState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IDiscussionState, formFilter: IFormFilter): IDiscussionState {

    const items: Dtos.Discussion[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            (p.comment && p.comment.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)
        )) : undefined;

    const newState: IDiscussionState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: IDiscussionState, propertiesToClear?: string[]): IDiscussionState {

    if (!propertiesToClear) {
        const newState: IDiscussionState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IDiscussionState, _isSaving: boolean): IDiscussionState {
    const newState: IDiscussionState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IDiscussionState,
    formData: Dtos.Discussion | Dtos.Discussion[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IDiscussionState {
    const newState: IDiscussionState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IDiscussionState, responseStatus: Dtos.ResponseStatus): IDiscussionState {
    const newState: IDiscussionState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IDiscussionState, _isCreating: boolean): IDiscussionState {
    const newState: IDiscussionState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IDiscussionState,
    formData: Dtos.Discussion,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IDiscussionState {
    const newState: IDiscussionState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IDiscussionState, responseStatus: Dtos.ResponseStatus): IDiscussionState {
    const newState: IDiscussionState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IDiscussionState, _isValidating: boolean): IDiscussionState {
    const newState: IDiscussionState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IDiscussionState,
    formData: Dtos.Discussion,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IDiscussionState {
    const newState: IDiscussionState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IDiscussionState, responseStatus: Dtos.ResponseStatus): IDiscussionState {
    const newState: IDiscussionState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const DiscussionReducer: Reducer<IDiscussionState> = (state: IDiscussionState = initialState, action: DiscussionAction) => {
    switch (action.type) {
        case ACTION_DISCUSSION_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_DISCUSSION_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_DISCUSSION_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_DISCUSSION_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_DISCUSSION_SAVE:
            return saving(state, true);
        case ACTION_DISCUSSION_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_DISCUSSION_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_DISCUSSION_CREATE:
            return creating(state, true);
        case ACTION_DISCUSSION_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_DISCUSSION_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_DISCUSSION_VALIDATE:
            return validating(state, true);
        case ACTION_DISCUSSION_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_DISCUSSION_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_DISCUSSION_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default DiscussionReducer;